import React, { useEffect } from 'react';
import { usePreferences } from '@context/preferences.context';
import { getLocaleByLanguage } from '@helpers/localization';
import Script from 'next/script';
import { useQuery } from '@tanstack/react-query';
import ChatApiService from '@services/chat-service';
import useAnalyticEvents from '@hooks/analytics/use-analytic-events';
import Environment from '@config/environment';
import useUser from '@hooks/user/use-user';
import { AvailableEnvironmentNames } from '@interfaces/models/environment';

const zendeskConfig: Partial<Record<AvailableEnvironmentNames, { languageFieldId: string }>> = {
  dev: {
    languageFieldId: '27578003350417',
  },
  production: {
    languageFieldId: '27751288734609',
  },
};

const ZendeskWebWidget: React.FC = () => {
  const { sendAnalyticEvent } = useAnalyticEvents('footer');

  const { language } = usePreferences();

  const { isAuthenticated } = useUser();

  const { data } = useQuery({
    queryKey: ['GET_ZENDESK_WIDGET_TOKEN_QUERY', isAuthenticated],
    queryFn: () => ChatApiService.getZendeskWidgetToken(),
    enabled: !!isAuthenticated,
  });

  const config = zendeskConfig[Environment.envName];

  useEffect(() => {
    if (window.zE && data?.token) {
      window.zE('messenger', 'loginUser', function (callback: Function) {
        callback(data.token);
      });

      window.zE('messenger:on', 'unreadMessages', function (count) {
        if (count) {
          sendAnalyticEvent('contact_us', { category: 'contact_us', action: 'start_chat', label: 'zendesk-widget' });
          window.zE('messenger', 'show');
        }
      });
    }
    // need window.zE as a dependency
    // eslint-disable-next-line
  }, [window.zE, data]);

  useEffect(() => {
    if (language && window.zE && config) {
      const lang = getLocaleByLanguage(language, 'en').toLowerCase();
      window.zE('messenger:set', 'locale', lang);
      window.zE('messenger:set', 'conversationFields', [{ id: config.languageFieldId, value: language }]);
    }
    // need window.zE as a dependency
    // eslint-disable-next-line
  }, [language, window.zE, config]);

  if (!isAuthenticated) {
    return null;
  }

  const zendeskScriptSrc = `https://static.zdassets.com/ekr/snippet.js?key=${Environment.zendesk.webWidgetId}`;

  return (
    <Script
      id="ze-snippet"
      onLoad={() => {
        window.zE?.('messenger', 'hide');
      }}
      src={zendeskScriptSrc}
    />
  );
};

export default ZendeskWebWidget;
